import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import "./CompanyPreferenceSettings.css";

const API_URL = "/api";

const emptyForm = {
  language: "Español",
  theme: "light",
  primary_color: "#1f5d7a",
  compact_mode: false,
  date_format: "DD/MM/YYYY",
  time_format: "24h",
  default_table_page_size: 10,
  is_active: true,
};

const toText = (value) => value ?? "";

const normalizePreferences = (data = {}) => ({
  ...emptyForm,
  ...data,
  language: toText(data.language) || "Español",
  theme: toText(data.theme) || "light",
  primary_color: toText(data.primary_color) || "var(--company-primary)",
  date_format: toText(data.date_format) || "DD/MM/YYYY",
  time_format: toText(data.time_format) || "24h",
  compact_mode: data.compact_mode ?? false,
  default_table_page_size: Number(data.default_table_page_size ?? 10),
  is_active: data.is_active ?? true,
});

function CompanyPreferenceSettings({ readOnly = false }) {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("company_token")}`,
  });

  const applyAppearance = useCallback((preferences) => {
    document.documentElement.style.setProperty(
      "--company-primary-color",
      preferences.primary_color || "var(--company-primary)"
    );

    document.body.dataset.theme = preferences.theme || "light";

    document.body.classList.toggle(
      "compact-mode",
      Boolean(preferences.compact_mode)
    );
  }, []);

  const loadPreferences = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/settings/preferences/me`,
        { headers: getHeaders() }
      );

      const preferences = normalizePreferences(response.data);

      setForm(preferences);
      applyAppearance(preferences);
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible cargar las preferencias."
      );
    } finally {
      setLoading(false);
    }
  }, [applyAppearance]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((currentForm) => {
      const updatedForm = {
        ...currentForm,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "primary_color") {
        document.documentElement.style.setProperty(
          "--company-primary-color",
          value || "var(--company-primary)"
        );
      }

      if (name === "theme") {
        document.body.dataset.theme = value;
      }

      if (name === "compact_mode") {
        document.body.classList.toggle("compact-mode", checked);
      }

      return updatedForm;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (readOnly) return;

    try {
      setSaving(true);

      const response = await axios.put(
        `${API_URL}/settings/preferences/me`,
        {
          ...form,
          default_table_page_size: Number(
            form.default_table_page_size
          ),
        },
        { headers: getHeaders() }
      );

      const preferences = normalizePreferences(response.data);

      setForm(preferences);
      applyAppearance(preferences);

      toast.success("Preferencias guardadas correctamente.");
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible guardar las preferencias."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        Cargando preferencias...
      </div>
    );
  }

  return (
    <section className="company-preference-settings">
      <div className="preference-settings-header">
        <div>
          <span className="settings-eyebrow">PREFERENCIAS</span>
          <h2>Idioma y apariencia</h2>
          <p>
            Personaliza la visualización del sistema para esta empresa.
          </p>
        </div>
      </div>

      <form
        className="preference-settings-form"
        onSubmit={handleSubmit}
      >
        <fieldset
          className="preference-settings-fieldset"
          disabled={readOnly}
        >
          <div className="preference-section">
            <div className="preference-section-title">
              <h3>Idioma y formatos</h3>
              <p>Define cómo se muestran fechas, horas y tablas.</p>
            </div>

            <div className="preference-grid">
              <label className="preference-field">
                <span>Idioma</span>
                <select
                  name="language"
                  value={form.language}
                  onChange={handleChange}
                >
                  <option value="Español">Español</option>
                  <option value="English">English</option>
                </select>
              </label>

              <label className="preference-field">
                <span>Formato de fecha</span>
                <select
                  name="date_format"
                  value={form.date_format}
                  onChange={handleChange}
                >
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </label>

              <label className="preference-field">
                <span>Formato de hora</span>
                <select
                  name="time_format"
                  value={form.time_format}
                  onChange={handleChange}
                >
                  <option value="24h">24 horas</option>
                  <option value="12h">12 horas (AM/PM)</option>
                </select>
              </label>

              <label className="preference-field">
                <span>Registros por tabla</span>
                <select
                  name="default_table_page_size"
                  value={form.default_table_page_size}
                  onChange={handleChange}
                >
                  <option value={10}>10 registros</option>
                  <option value={25}>25 registros</option>
                  <option value={50}>50 registros</option>
                  <option value={100}>100 registros</option>
                </select>
              </label>
            </div>
          </div>

          <div className="preference-section">
            <div className="preference-section-title">
              <h3>Apariencia</h3>
              <p>Los cambios se aplican inmediatamente como vista previa.</p>
            </div>

            <div className="preference-grid">
              <label className="preference-field">
                <span>Tema</span>
                <select
                  name="theme"
                  value={form.theme}
                  onChange={handleChange}
                >
                  <option value="light">Claro</option>
                  <option value="dark">Oscuro</option>
                  <option value="system">Según el sistema</option>
                </select>
              </label>

              <label className="preference-field">
                <span>Color principal</span>

                <div className="color-input-wrapper">
                  <input
                    type="color"
                    name="primary_color"
                    value={form.primary_color}
                    onChange={handleChange}
                    aria-label="Color principal"
                  />

                  <input
                    type="text"
                    name="primary_color"
                    value={form.primary_color}
                    onChange={handleChange}
                    maxLength={7}
                    placeholder="var(--company-primary)"
                  />
                </div>
              </label>

              <label className="preference-check-field">
                <input
                  type="checkbox"
                  name="compact_mode"
                  checked={form.compact_mode}
                  onChange={handleChange}
                />
                <span>
                  <strong>Modo compacto</strong>
                  <small>Reduce espacios en tablas y formularios.</small>
                </span>
              </label>

              <label className="preference-check-field">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                />
                <span>
                  <strong>Preferencias activas</strong>
                  <small>Permite aplicar esta configuración a la empresa.</small>
                </span>
              </label>
            </div>
          </div>
        </fieldset>

        <div className="preference-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={loadPreferences}
            disabled={saving}
          >
            Actualizar
          </button>

          {!readOnly ? (
            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
            >
              {saving
                ? "Guardando..."
                : "Guardar preferencias"}
            </button>
          ) : (
            <span className="settings-view-only">
              Solo consulta
            </span>
          )}
        </div>
      </form>
    </section>
  );
}

export default CompanyPreferenceSettings;